import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Archive, Trash2, MoreVertical } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;

export default function BrandList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/brands`,
        { withCredentials: true }
      );
      setBrands(response.data);
    } catch (err) {
      console.error('Error loading brands:', err);
      toast.error('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (brandId) => {
    if (!confirm('Archive this brand? All content will remain but the brand will be hidden.')) {
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/brands/${brandId}/archive`,
        {},
        { withCredentials: true }
      );
      toast.success('Brand archived');
      loadBrands();
    } catch (err) {
      toast.error('Failed to archive brand');
    }
  };

  const handleDelete = async (brandId) => {
    if (!confirm('PERMANENTLY DELETE this brand? This cannot be undone!')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/brands/${brandId}`,
        { withCredentials: true }
      );
      toast.success('Brand deleted');
      loadBrands();
    } catch (err) {
      toast.error('Failed to delete brand');
    }
  };

  const handleSwitch = (brandId) => {
    // Store active brand in localStorage
    localStorage.setItem('active_brand_id', brandId);
    toast.success('Brand switched!');
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Brands & Apps</h1>
          <p className="text-gray-400">Manage your independent brands with isolated content</p>
        </div>
        <button
          onClick={() => navigate('/brands/new')}
          className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors inline-flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          New Brand
        </button>
      </div>

      {/* Brands Grid */}
      {brands.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
            <Plus className="w-8 h-8 text-gray-500" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No brands yet</h3>
          <p className="text-gray-400 mb-6">Create your first brand to get started</p>
          <button
            onClick={() => navigate('/brands/new')}
            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create First Brand
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {brands.map(brand => (
            <div
              key={brand.id}
              className="bg-[#1a1d23] border border-gray-800 rounded-lg p-6 hover:border-cyan-600 transition-colors relative"
            >
              {/* Menu */}
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => setOpenMenu(openMenu === brand.id ? null : brand.id)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-5 h-5 text-gray-400" />
                </button>
                
                {openMenu === brand.id && (
                  <div className="absolute right-0 mt-2 w-48 bg-[#1a1d23] border border-gray-800 rounded-lg shadow-lg z-10">
                    <button
                      onClick={() => {
                        setOpenMenu(null);
                        navigate(`/brands/${brand.id}/edit`);
                      }}
                      className="w-full px-4 py-2 text-left text-white hover:bg-gray-800 flex items-center gap-2 rounded-t-lg"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setOpenMenu(null);
                        handleArchive(brand.id);
                      }}
                      className="w-full px-4 py-2 text-left text-yellow-400 hover:bg-gray-800 flex items-center gap-2"
                    >
                      <Archive className="w-4 h-4" />
                      Archive
                    </button>
                    <button
                      onClick={() => {
                        setOpenMenu(null);
                        handleDelete(brand.id);
                      }}
                      className="w-full px-4 py-2 text-left text-red-400 hover:bg-gray-800 flex items-center gap-2 rounded-b-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>

              {/* Logo */}
              {brand.logo_url ? (
                <img
                  src={brand.logo_url}
                  alt={brand.name}
                  className="w-16 h-16 object-contain mb-4 rounded-lg bg-gray-800"
                />
              ) : (
                <div
                  className="w-16 h-16 mb-4 rounded-lg flex items-center justify-center text-2xl font-bold text-white"
                  style={{ backgroundColor: brand.primary_color }}
                >
                  {brand.name.charAt(0)}
                </div>
              )}

              {/* Name & Slug */}
              <h3 className="text-xl font-bold text-white mb-1">{brand.name}</h3>
              <p className="text-sm text-gray-400 mb-4">/{brand.slug}</p>

              {/* Colors */}
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="w-8 h-8 rounded"
                  style={{ backgroundColor: brand.primary_color }}
                  title="Primary color"
                />
                <div
                  className="w-8 h-8 rounded"
                  style={{ backgroundColor: brand.secondary_color }}
                  title="Secondary color"
                />
              </div>

              {/* CTA */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-1">Default CTA:</p>
                <p className="text-sm text-white">"{brand.cta_default_text}"</p>
              </div>

              {/* Switch Button */}
              <button
                onClick={() => handleSwitch(brand.id)}
                className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
              >
                Switch to this Brand
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
